import {
  useRef,
  useState,
  useEffect,
  useMemo,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import clsx from 'classnames';
import { Badge } from '@cowbell/ui';
import { useMantineColorScheme } from '@mantine/core'; // eslint-disable-line cowbell/restricted-imports-error -- no @cowbell/ui equivalent for this hook
import {
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  BarStack,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import type { ChartAction, ChartConfig } from '../../types';
import styles from './ActionCard.module.css';

// Shared chrome for the `table` and `matrix` subType renderers (both draw a
// card-wrapped HTML table) — extracted to keep the two branches DRY.
const CARD_TABLE_STYLE: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 'var(--mantine-font-size-xs)',
};

// OMNI-989: the scroll parent is what keeps a wide table inside the card.
// `CARD_TABLE_STYLE` sets `width: 100%`, but under the default
// `table-layout: auto` that is only a *preferred* width — the browser widens
// the table past it whenever the columns' min-content widths sum to more
// (a matrix of 8 named actors at panel width does exactly that). `.card` sets
// no `overflow`, so without this container the surplus columns paint outside
// the card border, run past the chat panel edge, and cannot be reached.
// `maxWidth` is belt-and-braces should `.card` ever become a flex/grid parent.
// Mirrors `AssistantMDTable`, which already does this for the markdown tables
// rendered into the same message node.
const CARD_TABLE_SCROLL_STYLE: CSSProperties = {
  overflowX: 'auto',
  maxWidth: '100%',
};

/** Card shell shared by the `table` and `matrix` subTypes — both draw a titled
 *  card whose entire body is one HTML table, followed by an optional caption.
 *  `children` are the table's own `thead`/`tbody`. */
function TableCard({
  containerRef,
  title,
  badge,
  badgeColor,
  caption,
  children,
}: Readonly<{
  containerRef: RefObject<HTMLDivElement>;
  title?: string | null;
  badge?: string | null;
  badgeColor?: string | null;
  caption?: string;
  children: ReactNode;
}>) {
  return (
    <div ref={containerRef} className={styles.card}>
      <CardTitle title={title} badge={badge} badgeColor={badgeColor} />
      {/* `data-print-table-wrapper` is what MessageFeedback's print stylesheet
       *  keys off to restore `overflow-x: visible` / `max-width: none`. Without
       *  it the scroll container below would clip the off-screen columns out of
       *  a printed message — they scroll on screen but paper cannot scroll. */}
      <div style={CARD_TABLE_SCROLL_STYLE} data-print-table-wrapper>
        <table style={CARD_TABLE_STYLE}>{children}</table>
      </div>
      {caption && <div className={styles.chartCaption}>{caption}</div>}
    </div>
  );
}

function cellStyle(
  align: CSSProperties['textAlign'],
  opts: { header?: boolean; bold?: boolean } = {}
): CSSProperties {
  return {
    textAlign: align,
    padding: opts.header ? '6px 8px' : '4px 8px',
    borderBottom: '1px solid var(--mantine-color-default-border)',
    ...(opts.header || opts.bold ? { fontWeight: 600 } : {}),
  };
}

function CardTitle({
  title,
  badge,
  badgeColor,
}: {
  title?: string | null;
  badge?: string | null;
  badgeColor?: string | null;
}) {
  if (!title) return null;
  return (
    <div className={styles.cardTitle}>
      {badge && (
        <Badge size="xs" color={badgeColor || 'blue'} mr="xs">
          {badge}
        </Badge>
      )}
      {title}
    </div>
  );
}

interface Props {
  action: ChartAction;
}

const CHART_COLORS = [
  '#0072B2', // blue
  '#E69F00', // orange
  '#009E73', // green
  '#CC79A7', // pink
  '#56B4E9', // sky blue
  '#D55E00', // vermilion
  '#F0E442', // yellow
  '#000000', // black
];

export function pickCellColor(
  datum: Record<string, unknown>,
  seriesColor: string | undefined,
  i: number
): string {
  return (
    (datum.color as string) ||
    seriesColor ||
    CHART_COLORS[i % CHART_COLORS.length]
  );
}

function getTooltipStyle(dark: boolean) {
  return {
    backgroundColor: dark ? '#25262b' : '#fff',
    border: `1px solid ${dark ? '#373A40' : '#dee2e6'}`,
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 12,
  };
}

/** Rounded top corners on a bar. A stack takes them once, on the whole stack,
 *  so its segments sit flush and the topmost visible one gets the cap. */
const BAR_TOP_RADIUS: [number, number, number, number] = [3, 3, 0, 0];

/** A stacked bar's tooltip lists the segments top first, as they read on the
 *  bar, followed by their total. */
export function stackedTooltipRows<T extends { value?: unknown }>(
  payload: T[]
): { rows: T[]; total: number } {
  const total = payload.reduce(
    (sum, entry) => sum + (typeof entry.value === 'number' ? entry.value : 0),
    0
  );
  return { rows: [...payload].reverse(), total };
}

function ChartTooltip({ active, payload, label, dark, stacked }: any) {
  if (!active || !payload?.length) return null;
  const { rows, total } = stacked
    ? stackedTooltipRows(payload)
    : { rows: payload, total: null };
  return (
    <div style={getTooltipStyle(dark)}>
      {label && (
        <div
          style={{
            fontWeight: 600,
            marginBottom: 4,
            fontSize: 12,
            color: dark ? '#C1C2C5' : '#212529',
          }}
        >
          {label}
        </div>
      )}
      {rows.map((entry: any, i: number) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 2,
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: entry.color || CHART_COLORS[i],
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 12, color: dark ? '#909296' : '#868e96' }}>
            {entry.name}:
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: dark ? '#C1C2C5' : '#212529',
            }}
          >
            {entry.value}
          </span>
        </div>
      ))}
      {total !== null && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            fontWeight: 600,
            color: dark ? '#C1C2C5' : '#212529',
          }}
        >
          Total: {total}
        </div>
      )}
    </div>
  );
}

/** Horizontal room for the spoke labels that recharts draws outside the radius. */
const RADAR_MARGIN = { top: 8, right: 40, bottom: 8, left: 40 };

/** Assumed card width until the ResizeObserver reports one — chosen to keep the
 *  previous 250px starting chart height (455 × 0.55). */
const DEFAULT_CARD_WIDTH = 455;

/** Width the side-data table takes out of the card: `.sidePanel`'s 170px plus
 *  `.chartWithSide`'s 12px gap. Its 12px padding and 1px border are already
 *  inside the 170px — Mantine's reset puts everything on `border-box`. */
const SIDE_PANEL_TOTAL = 182;

/**
 * Card width below which the side-data table moves under the chart instead of
 * beside it. A radar needs the higher threshold because the plot has to stay
 * wide enough for the spoke labels drawn outside the radius; cartesian charts
 * read fine in the narrower box, so they keep the original value.
 */
const SIDE_DATA_STACK_BELOW = 480;
const RADAR_SIDE_DATA_STACK_BELOW = 520;

/**
 * Cap on a card that carries a side-data table. For a radar, ~650 is where the
 * height cap takes over as the binding dimension, so a wider card would only add
 * whitespace rather than a bigger plot. Other chart types keep the original cap.
 */
const CARD_MAX_WIDTH_WITH_SIDE = 600;
const RADAR_CARD_MAX_WIDTH_WITH_SIDE = 660;

/** Plot height as a fraction of width. A radar's radius is bound by the
 *  *smaller* of width/height, so it wants a near-square box measured against the
 *  width the plot actually gets; cartesian charts read better wide and short and
 *  keep their original card-width basis. */
const RADAR_ASPECT = 0.85;
const CARTESIAN_ASPECT = 0.55;
const MIN_CHART_HEIGHT = 200;
const MAX_CHART_HEIGHT = 400;

function cardSizeStyle(isRadar: boolean): CSSProperties {
  if (!isRadar) return { maxWidth: CARD_MAX_WIDTH_WITH_SIDE };
  return {
    maxWidth: RADAR_CARD_MAX_WIDTH_WITH_SIDE,
    marginInline: 'auto',
  };
}

/**
 * Angle for the radial tick labels (0/25/50/…). Recharts defaults to 0°, which
 * on an even spoke count is exactly the right-hand spoke — so the ticks land on
 * top of that spoke's label. Aim halfway between the first two spokes instead:
 * spokes run clockwise from 90°, one every 360/n, so 90 - 180/n is always in a
 * gap (67.5° for the 8 Cowbell Factors). The guard is only for an empty
 * dataset, where the division would yield -Infinity.
 */
function radiusAxisAngle(spokeCount: number) {
  return spokeCount > 0 ? 90 - 180 / spokeCount : 90;
}

function renderRadarChart(
  data: Record<string, unknown>[],
  config: ChartConfig,
  dark: boolean,
  chartHeight: number
) {
  const { series = [], angleKey = 'factor', domain } = config;
  const textColor = dark ? '#909296' : '#868e96';

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      {/* Spoke labels are drawn OUTSIDE the outer radius, and recharts' polar
          default margin is only 5px — so the horizontal spokes (e.g. "Endpoint"
          right, "Compliance" left on an 8-factor chart) clip at the plot edge.
          Reserve a horizontal gutter and pull the radius in to fit them. */}
      <RadarChart
        data={data}
        cx="50%"
        cy="50%"
        outerRadius="76%"
        margin={RADAR_MARGIN}
      >
        <PolarGrid stroke={dark ? '#373A40' : '#dee2e6'} />
        <PolarAngleAxis
          dataKey={angleKey}
          tick={{ fill: textColor, fontSize: 10 }}
        />
        <PolarRadiusAxis
          angle={radiusAxisAngle(data.length)}
          domain={domain || [0, 100]}
          tick={{ fill: textColor, fontSize: 9 }}
          axisLine={false}
        />
        {series.map((s, i) => (
          <Radar
            key={s.key}
            name={s.label}
            dataKey={s.key}
            stroke={s.color || CHART_COLORS[i]}
            fill={s.color || CHART_COLORS[i]}
            fillOpacity={i === 0 ? 0.25 : 0.1}
            strokeWidth={2}
          />
        ))}
        <Tooltip content={<ChartTooltip dark={dark} />} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

function renderBarChart(
  data: Record<string, unknown>[],
  config: ChartConfig,
  dark: boolean,
  chartHeight: number,
  stacked = false
) {
  const { series = [], xKey = 'label', yAxisLabel, domain } = config;
  const textColor = dark ? '#909296' : '#868e96';

  const unstackedBarSize = series.length > 1 ? 20 : 32;
  const bars = series.map((s, si) => (
    <Bar
      key={s.key}
      dataKey={s.key}
      name={s.label}
      // Colours a stacked segment, and the legend swatch of every bar: without
      // it the swatch is black even when Cells colour the bars.
      fill={s.color || CHART_COLORS[si % CHART_COLORS.length]}
      radius={stacked ? undefined : BAR_TOP_RADIUS}
      barSize={stacked ? undefined : unstackedBarSize}
      maxBarSize={stacked ? 32 : undefined}
    >
      {/* Per-datum colours only on unstacked bars: in a stack they would paint
          every segment of a bar alike and erase the breakdown. */}
      {!stacked &&
        data.map((d, i) => (
          <Cell key={i} fill={pickCellColor(d, s.color, si)} />
        ))}
    </Bar>
  ));

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={dark ? '#373A40' : '#dee2e6'}
        />
        <XAxis dataKey={xKey} tick={{ fill: textColor, fontSize: 11 }} />
        <YAxis
          domain={domain ?? undefined}
          tick={{ fill: textColor, fontSize: 11 }}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  fill: textColor,
                  fontSize: 11,
                }
              : undefined
          }
        />
        {stacked ? (
          // BarStack rounds the outer corners of each whole stack, so a bar
          // whose last series is zero still gets a rounded top. Its stackId is
          // left unset: recharts names the clip path after it, and a shared
          // name would clip one chart with another's path.
          <BarStack radius={BAR_TOP_RADIUS}>{bars}</BarStack>
        ) : (
          bars
        )}
        <Legend wrapperStyle={{ fontSize: 11, color: textColor }} />
        <Tooltip content={<ChartTooltip dark={dark} stacked={stacked} />} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function renderPieChart(
  data: Record<string, unknown>[],
  config: ChartConfig,
  dark: boolean,
  chartHeight: number
) {
  const { nameKey = 'name', valueKey = 'value' } = config;
  const textColor = dark ? '#909296' : '#868e96';

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={80}
          innerRadius={45}
          paddingAngle={2}
          label={({ name, percent }: any) =>
            `${name} ${(percent * 100).toFixed(0)}%`
          }
          labelLine={true}
          fontSize={11}
          fill={textColor}
        >
          {data.map((d, i) => (
            <Cell key={i} fill={pickCellColor(d, undefined, i)} />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltip dark={dark} />} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function renderLineChart(
  data: Record<string, unknown>[],
  config: ChartConfig,
  dark: boolean,
  chartHeight: number
) {
  const { series = [], xKey = 'label', yAxisLabel, domain } = config;
  const textColor = dark ? '#909296' : '#868e96';

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <LineChart
        data={data}
        margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={dark ? '#373A40' : '#dee2e6'}
        />
        <XAxis dataKey={xKey} tick={{ fill: textColor, fontSize: 11 }} />
        <YAxis
          domain={domain ?? undefined}
          tick={{ fill: textColor, fontSize: 11 }}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  fill: textColor,
                  fontSize: 11,
                }
              : undefined
          }
        />
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color || CHART_COLORS[i]}
            strokeWidth={2}
            dot={{ fill: s.color || CHART_COLORS[i], r: 3 }}
          />
        ))}
        <Legend wrapperStyle={{ fontSize: 11, color: textColor }} />
        <Tooltip content={<ChartTooltip dark={dark} />} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function renderAreaChart(
  data: Record<string, unknown>[],
  config: ChartConfig,
  dark: boolean,
  chartHeight: number
) {
  const { series = [], xKey = 'label', yAxisLabel, domain } = config;
  const textColor = dark ? '#909296' : '#868e96';

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <AreaChart
        data={data}
        margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
      >
        <defs>
          {series.map((s, i) => {
            const color = s.color || CHART_COLORS[i];
            return (
              <linearGradient
                key={s.key}
                id={`areaGrad-${s.key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={dark ? '#373A40' : '#dee2e6'}
        />
        <XAxis dataKey={xKey} tick={{ fill: textColor, fontSize: 11 }} />
        <YAxis
          domain={domain ?? undefined}
          tick={{ fill: textColor, fontSize: 11 }}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  fill: textColor,
                  fontSize: 11,
                }
              : undefined
          }
        />
        {series.map((s, i) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color || CHART_COLORS[i]}
            strokeWidth={2}
            fill={`url(#areaGrad-${s.key})`}
            dot={{ fill: s.color || CHART_COLORS[i], r: 2 }}
          />
        ))}
        <Legend wrapperStyle={{ fontSize: 11, color: textColor }} />
        <Tooltip content={<ChartTooltip dark={dark} />} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function renderChart(
  chartType: string,
  data: Record<string, unknown>[],
  config: ChartConfig,
  dark: boolean,
  chartHeight: number
) {
  switch (chartType) {
    case 'radar':
      return renderRadarChart(data, config, dark, chartHeight);
    case 'bar':
    case 'groupedBar':
      return renderBarChart(data, config, dark, chartHeight);
    case 'stackedBar':
      return renderBarChart(data, config, dark, chartHeight, true);
    case 'pie':
      return renderPieChart(data, config, dark, chartHeight);
    case 'line':
      return renderLineChart(data, config, dark, chartHeight);
    case 'area':
      return renderAreaChart(data, config, dark, chartHeight);
    default:
      return null;
  }
}

function formatDelta(
  value: number,
  baseline: number
): { text: string; color: string } {
  const delta = Math.round(value - baseline);
  if (delta > 0)
    return { text: `+${delta}`, color: 'var(--mantine-color-teal-6)' };
  if (delta < 0)
    return { text: `${delta}`, color: 'var(--mantine-color-red-6)' };
  return { text: '0', color: 'var(--mantine-color-dimmed)' };
}

export default function ChartCard({ action }: Props) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  const containerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(DEFAULT_CARD_WIDTH);

  // Every sizing tweak below is scoped to the radar (OMNI-689): it is the only
  // chart type whose plot is bound by the *smaller* of width/height and that
  // draws labels outside the plot. Other chart types keep their existing
  // geometry exactly, side-data table or not.
  const isRadar = action.chartType === 'radar';
  const hasSideData = Boolean(action.sideData?.length);
  // Beside the chart on a wide card, underneath it once the card is narrow —
  // otherwise the fixed-width table starves the plot.
  const stackSideData =
    hasSideData &&
    cardWidth < (isRadar ? RADAR_SIDE_DATA_STACK_BELOW : SIDE_DATA_STACK_BELOW);
  // For the radar, height comes from the width the plot actually gets: with the
  // table beside it the plot is ~195px narrower, and sizing off the card made
  // the box too short, capping the radius while horizontal space went unused.
  const radarPlotWidth =
    hasSideData && !stackSideData
      ? Math.max(0, cardWidth - SIDE_PANEL_TOTAL)
      : cardWidth;
  const chartHeight = Math.max(
    MIN_CHART_HEIGHT,
    Math.min(
      MAX_CHART_HEIGHT,
      Math.round(
        isRadar ? radarPlotWidth * RADAR_ASPECT : cardWidth * CARTESIAN_ASPECT
      )
    )
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Rounded so sub-pixel jitter during a panel drag doesn't re-render on
    // every fractional change (`contentRect.width` is fractional).
    const observer = new ResizeObserver(([entry]) => {
      setCardWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedData = useMemo(() => {
    if (!sortCol || !action.data) return action.data;
    return [...action.data].sort((a, b) => {
      const av = a[sortCol],
        bv = b[sortCol];
      const cmp =
        typeof av === 'number'
          ? av - (bv as number)
          : String(av ?? '').localeCompare(String(bv ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [action.data, sortCol, sortDir]);

  // Data validation
  if (!action.data || !Array.isArray(action.data) || action.data.length === 0) {
    return (
      <div ref={containerRef} className={styles.card}>
        <div className={styles.emptyState}>No data available</div>
      </div>
    );
  }

  // Table subType renders as HTML table
  if (action.subType === 'table' && action.config.columns) {
    return (
      <TableCard
        containerRef={containerRef}
        title={action.title}
        badge={action.badge}
        badgeColor={action.badgeColor}
        caption={action.caption}
      >
        <thead>
          <tr>
            {action.config.columns.map((col) => (
              <th
                key={col.key}
                onClick={() => {
                  if (sortCol === col.key)
                    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                  else {
                    setSortCol(col.key);
                    setSortDir('asc');
                  }
                }}
                style={{
                  ...cellStyle(col.align || 'left', { header: true }),
                  cursor: 'pointer',
                }}
              >
                {col.label}
                {sortCol === col.key
                  ? sortDir === 'asc'
                    ? ' \u25B2'
                    : ' \u25BC'
                  : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => (
            <tr key={idx}>
              {action.config.columns!.map((col) => (
                <td key={col.key} style={cellStyle(col.align || 'left')}>
                  {String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableCard>
    );
  }

  // Matrix subType renders as an industry × actor grid of ●/– cells.
  if (action.subType === 'matrix' && action.config.series) {
    const xKey = action.config.xKey || 'label';
    const cols = action.config.series;
    const accent = dark ? '#f5a3a3' : '#c2410c';
    const muted = dark ? '#5c5f66' : '#adb5bd';
    return (
      <TableCard
        containerRef={containerRef}
        title={action.title}
        caption={action.caption}
      >
        <thead>
          <tr>
            <th style={cellStyle('left', { header: true })} />
            {cols.map((c) => (
              <th key={c.key} style={cellStyle('center', { header: true })}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {action.data.map((row, idx) => (
            <tr key={idx}>
              <td style={cellStyle('left', { bold: true })}>
                {String(row[xKey] ?? '')}
              </td>
              {cols.map((c) => (
                <td key={c.key} style={cellStyle('center')}>
                  <span style={{ color: row[c.key] ? accent : muted }}>
                    {row[c.key] ? '●' : '–'}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableCard>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.card}
      // A radar earns a wider cap (see the constant) and is centred, because the
      // raised cap leaves slack on a wide response and collecting all of it on
      // one side reads as misalignment. Other chart types keep the original cap
      // and alignment.
      style={hasSideData ? cardSizeStyle(isRadar) : undefined}
    >
      {/* Header row: title + badge */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 4,
        }}
      >
        <div>
          {action.title && (
            <div style={{ fontSize: 13, fontWeight: 600 }}>{action.title}</div>
          )}
          {action.subtitle && (
            <div style={{ fontSize: 11, color: 'var(--mantine-color-dimmed)' }}>
              {action.subtitle}
            </div>
          )}
        </div>
        {action.badge && (
          <Badge size="xs" color={action.badgeColor || 'blue'}>
            {action.badge}
          </Badge>
        )}
      </div>

      {/* Chart + Side Data */}
      <div
        className={
          clsx(hasSideData && styles.chartWithSide, {
            [styles.chartSideStacked]: stackSideData,
          }) || undefined
        }
      >
        <div className={styles.chartContainer}>
          {renderChart(
            action.chartType,
            action.data,
            action.config,
            dark,
            chartHeight
          )}
        </div>
        {hasSideData && (
          <div className={styles.sidePanel}>
            {/* Inline styles rather than a shared rule because this table is
                deliberately denser than the card tables above: 10/11px text,
                2px 4px padding, no cell borders. (They were originally also
                needed to beat V1's `.assistantContent :global(table/th/td)`
                rules — those went when Moo chat v1 was removed, but the styles
                stay: they are what makes the side panel compact.) */}
            {/* `fixed` layout so the numeric columns can never be pushed out of
                the fixed-width panel: with `auto`, a long label (account names
                on a compare card, up to 20 chars) widened the table past the
                panel and the "vs Ind." column was clipped at the card edge. */}
            <table
              style={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                fontSize: 11,
                margin: 0,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      fontSize: 10,
                      fontWeight: 500,
                      color: 'var(--mantine-color-dimmed)',
                      padding: '2px 4px',
                      border: 'none',
                    }}
                  >
                    {action.sideHeader || 'Factor'}
                  </th>
                  {/* Widths live on the header row: under `fixed` layout the
                      first row's cells define the columns. The label column is
                      left unsized so it absorbs whatever is left. */}
                  <th
                    style={{
                      textAlign: 'right',
                      fontSize: 10,
                      width: 30,
                      padding: '2px 4px',
                      border: 'none',
                    }}
                  >
                    &nbsp;
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      fontSize: 10,
                      fontWeight: 500,
                      color: 'var(--mantine-color-dimmed)',
                      whiteSpace: 'nowrap',
                      width: 42,
                      padding: '2px 4px',
                      border: 'none',
                    }}
                  >
                    vs Ind.
                  </th>
                </tr>
              </thead>
              <tbody>
                {action.sideData!.map((item, idx) => {
                  const delta =
                    item.baseline != null
                      ? formatDelta(item.value, item.baseline)
                      : null;
                  return (
                    <tr key={idx}>
                      <td
                        style={{
                          // Wraps rather than `nowrap`: a long account name
                          // takes a second line instead of shoving the value
                          // and delta columns off the card.
                          overflowWrap: 'anywhere',
                          fontSize: 11,
                          padding: '2px 4px',
                          border: 'none',
                        }}
                      >
                        {item.label}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          fontSize: 11,
                          width: 30,
                          whiteSpace: 'nowrap',
                          padding: '2px 4px',
                          border: 'none',
                        }}
                      >
                        {item.value}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          fontSize: 11,
                          color: delta?.color,
                          width: 42,
                          whiteSpace: 'nowrap',
                          padding: '2px 4px',
                          border: 'none',
                        }}
                      >
                        {delta?.text ?? ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {action.caption && (
        <div className={styles.chartCaption}>{action.caption}</div>
      )}
    </div>
  );
}
